import type { NavTab } from '../ui/components/BottomNav';
import { getEnvironment } from './tokenStorage';
import { normalizeDocumentName } from './reviewDocumentIdentity';

export type SolutionsSubView = 'list' | 'master_cross_checks' | 'construction_loan';

export const SESSION_NAV_KEY = `courtyard_word_session_nav_${getEnvironment()}`;

export interface SessionNavigationSnapshot {
  viewMode: NavTab;
  solutionsSubView: SolutionsSubView;
  selectedProjectId: string;
  selectedSubProjectId: string;
  reviewKeepAlive: boolean;
  /**
   * Normalized basename of the document the selection belongs to. The project /
   * client-project selection is meaningful only for that document — opening a
   * different document must re-resolve it rather than inherit the last one.
   */
  documentName: string;
}

const DEFAULT_SNAPSHOT: SessionNavigationSnapshot = {
  viewMode: 'clauses',
  solutionsSubView: 'list',
  selectedProjectId: '',
  selectedSubProjectId: '',
  reviewKeepAlive: false,
  documentName: '',
};

const VALID_VIEW_MODES = new Set<NavTab>([
  'review',
  'editor',
  'agent',
  'ask',
  'clauses',
  'proofread',
  'profile',
]);

const VALID_SOLUTIONS_SUB_VIEWS = new Set<SolutionsSubView>([
  'list',
  'master_cross_checks',
  'construction_loan',
]);

function parseSnapshot(raw: string): SessionNavigationSnapshot | null {
  try {
    const data = JSON.parse(raw) as Partial<SessionNavigationSnapshot>;
    if (!data || typeof data !== 'object') return null;

    const viewMode =
      typeof data.viewMode === 'string' && VALID_VIEW_MODES.has(data.viewMode as NavTab)
        ? (data.viewMode as NavTab)
        : DEFAULT_SNAPSHOT.viewMode;

    const solutionsSubView =
      typeof data.solutionsSubView === 'string' &&
      VALID_SOLUTIONS_SUB_VIEWS.has(data.solutionsSubView as SolutionsSubView)
        ? (data.solutionsSubView as SolutionsSubView)
        : DEFAULT_SNAPSHOT.solutionsSubView;

    return {
      viewMode: viewMode === 'profile' ? 'review' : viewMode,
      solutionsSubView,
      selectedProjectId: typeof data.selectedProjectId === 'string' ? data.selectedProjectId : '',
      selectedSubProjectId: typeof data.selectedSubProjectId === 'string' ? data.selectedSubProjectId : '',
      reviewKeepAlive: Boolean(data.reviewKeepAlive),
      documentName:
        typeof data.documentName === 'string' ? normalizeDocumentName(data.documentName) : '',
    };
  } catch {
    return null;
  }
}

export function loadSessionNavigation(): SessionNavigationSnapshot {
  if (typeof window === 'undefined') return DEFAULT_SNAPSHOT;
  const raw = localStorage.getItem(SESSION_NAV_KEY);
  if (!raw) return DEFAULT_SNAPSHOT;
  return parseSnapshot(raw) ?? DEFAULT_SNAPSHOT;
}

export function saveSessionNavigation(snapshot: SessionNavigationSnapshot): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(SESSION_NAV_KEY, JSON.stringify(snapshot));
  } catch {
    // Ignore quota / private-mode errors
  }
}

/**
 * True when a restored snapshot's project selection belongs to the open document.
 *
 * A snapshot saved before this field existed (no `documentName`) is never trusted:
 * its selection may come from any document, so the add-in re-resolves instead.
 */
export function snapshotMatchesDocument(
  snapshot: SessionNavigationSnapshot,
  documentName: string | null | undefined,
): boolean {
  const stored = normalizeDocumentName(snapshot.documentName);
  const current = normalizeDocumentName(documentName ?? '');
  return stored !== '' && stored === current;
}

/**
 * True when a restored snapshot carries a selection actually worth restoring for
 * the open document — a chosen client project.
 *
 * Matching the document is not enough on its own: a snapshot can name this
 * document while holding only a solution and no project under it (the user
 * opened a solution, picked nothing, and closed). There is nothing to restore
 * from that, so the caller must re-resolve the document instead of treating the
 * empty selection as the user's place.
 */
export function snapshotHasRestorableSelection(
  snapshot: SessionNavigationSnapshot,
  documentName: string | null | undefined,
): boolean {
  return snapshotMatchesDocument(snapshot, documentName) && snapshot.selectedSubProjectId !== '';
}

/** Drops the per-document part of a snapshot, keeping the tab the user was on. */
export function clearDocumentScopedSelection(
  snapshot: SessionNavigationSnapshot,
): SessionNavigationSnapshot {
  return {
    ...snapshot,
    solutionsSubView: 'list',
    selectedProjectId: '',
    selectedSubProjectId: '',
    documentName: '',
  };
}
